import Layout from 'layout'
import { ReactElement } from 'react'
import { withServerSideProps } from 'shared/withPageProps'
import {
	landingContentFull,
	landingContentQa,
	LandingVariantContent,
} from 'utils/content'
import { styled } from '@mui/system'
import { GetServerSidePropsContext } from 'next'

import {
	Hero,
	Features,
	About,
	Dashboard,
	FreeTrial,
} from '@/components/simplified'

const PrimarySpan = styled('span')(({ theme }) => ({
	color: theme.palette.primary.main,
}))

export type Variant = 'qa' | 'full'

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? 'https://sqabot.ai'

function getVariantContent(variant: Variant): LandingVariantContent {
	return variant === 'qa' ? landingContentQa : landingContentFull
}

export const getServerSideProps = withServerSideProps(
	async (ctx: GetServerSidePropsContext) => {
		const raw =
			typeof ctx.query.v === 'string' ? ctx.query.v.toLowerCase() : ''
		const variant: Variant = raw === 'qa' ? 'qa' : 'full'
		return {
			props: { variant },
		}
	}
)

interface HomePageProps {
	variant: Variant
	isLoggedIn?: boolean
	user?: { name?: string } | null
}

export default function HomePage({ variant }: HomePageProps) {
	const content = getVariantContent(variant)

	return (
		<>
			<Hero
				eyebrow={content.hero.eyebrow}
				title={
					<>
						{content.hero.headline[0]} <br />
						{content.hero.headline[1]}
					</>
				}
				description={
					<>
						{content.hero.descriptionLead}{' '}
						<PrimarySpan>
							{content.hero.highlightPhrase}
						</PrimarySpan>{' '}
						of every build so you can deploy with confidence.
						{content.hero.earlyAccessIntro && (
							<>
								<br />
								<br />
								{content.hero.earlyAccessIntro
									.split('\n')
									.map((line, i) => (
										<span key={i}>
											{line}
											{i <
												content.hero.earlyAccessIntro!.split(
													'\n'
												).length -
													1 && <br />}
										</span>
									))}
								{content.hero.earlyAccessBullets && (
									<>
										<br />
										<br />
										{content.hero.earlyAccessBullets.map(
											(bullet, i) => (
												<span key={i}>
													⭐️ {bullet}
													{i <
														content.hero
															.earlyAccessBullets!
															.length -
															1 && <br />}
												</span>
											)
										)}
									</>
								)}
							</>
						)}
					</>
				}
				buttons={content.hero.buttons.map(btn => ({
					text: btn.text,
					href: btn.href,
					style: btn.style === 'primary' ? 'primary' : 'outlined',
					openInNewTab: btn.openInNewTab,
				}))}
				backgroundImage={content.hero.backgroundImage}
				layout="text-with-image"
			/>

			<Features
				title={
					<>
						{content.features.titleLine1}
						<br />
						{content.features.titleLine2}
					</>
				}
				description={content.features.description}
				columns={3}
				backgroundColor="default"
				features={content.features.cards.map(card => ({
					title: card.title,
					listItems: card.content,
					icon: card.icon,
				}))}
			/>

			<About
				title={content.about.title}
				content={content.about.body}
				image={content.about.image}
				imageCaption="ASTA start page interface"
				layout="text-above-image"
				backgroundColor="default"
				textAlign="center"
			/>

			<Dashboard
				title={content.dashboard.title}
				content={content.dashboard.body}
				image={content.dashboard.image}
				imageAlt="ASTA Dashboard"
				features={content.dashboard.checklist}
			/>

			<FreeTrial
				title={content.freeTrial.title}
				buttonText={content.freeTrial.buttonText}
			/>
		</>
	)
}

HomePage.getLayout = function getLayout(page: ReactElement) {
	const { variant } = page.props as HomePageProps
	const content = getVariantContent(variant)

	return (
		<Layout
			title={content.meta.title}
			description={content.meta.description}
			keywords={content.meta.keywords}
			canonical={variant === 'qa' ? SITE_URL : undefined}
			isLoggedIn={page.props.isLoggedIn}
			user={page.props.user}
		>
			{page}
		</Layout>
	)
}
